// =============================================================================
// MeuDriver — Unified Messages Inbox (student + instructor)
// =============================================================================

import { Link } from 'react-router-dom'
import { MessageCircle, ArrowLeft, Clock } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getInitials, cn } from '@/lib/utils'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Conversation {
  bookingId: string
  scheduledAt: string
  bookingStatus: string
  lastMessage: { content: string; created_at: string; sender_id: string } | null
  unreadCount: number
  otherName: string | null
  otherAvatar: string | null
  detailPath: string
}

// ─── Fetch logic ──────────────────────────────────────────────────────────────

async function fetchConversations(
  userId: string,
  role: 'student' | 'instructor' | 'admin',
): Promise<Conversation[]> {
  // 1. Fetch bookings for this user
  let bookingData: any[] = []

  if (role === 'student') {
    const { data } = await supabase
      .from('bookings')
      .select(`
        id, scheduled_at, status,
        instructor:instructors!bookings_instructor_id_fkey(
          profile:profiles(full_name, avatar_url)
        )
      `)
      .eq('student_id', userId)
      .order('scheduled_at', { ascending: false })
    bookingData = data ?? []
  } else if (role === 'instructor') {
    // First get the instructors row id
    const { data: instRow } = await supabase
      .from('instructors')
      .select('id')
      .eq('profile_id', userId)
      .maybeSingle()
    if (!instRow) return []

    const { data } = await supabase
      .from('bookings')
      .select(`
        id, scheduled_at, status,
        student:profiles!bookings_student_id_fkey(full_name, avatar_url)
      `)
      .eq('instructor_id', instRow.id)
      .order('scheduled_at', { ascending: false })
    bookingData = data ?? []
  }

  if (bookingData.length === 0) return []
  const bookingIds = bookingData.map((b: any) => b.id)

  // 2. Fetch all messages for those bookings
  const { data: messages } = await supabase
    .from('messages')
    .select('booking_id, content, created_at, sender_id, read_at')
    .in('booking_id', bookingIds)
    .order('created_at', { ascending: false })

  if (!messages || messages.length === 0) return []

  // 3. Group by booking_id
  const bookingMap = new Map(bookingData.map((b: any) => [b.id, b]))
  const msgByBooking = new Map<string, typeof messages>()
  for (const m of messages) {
    if (!msgByBooking.has(m.booking_id)) msgByBooking.set(m.booking_id, [])
    msgByBooking.get(m.booking_id)!.push(m)
  }

  // 4. Build conversation list
  const convos: Conversation[] = []
  for (const [bookingId, msgs] of msgByBooking) {
    const booking = bookingMap.get(bookingId)
    if (!booking) continue

    const lastMsg = msgs[0]
    const unread = msgs.filter((m) => !m.read_at && m.sender_id !== userId).length

    const other =
      role === 'student'
        ? (booking.instructor as any)?.profile
        : (booking.student as any)

    convos.push({
      bookingId,
      scheduledAt: booking.scheduled_at,
      bookingStatus: booking.status,
      lastMessage: lastMsg,
      unreadCount: unread,
      otherName: other?.full_name ?? null,
      otherAvatar: other?.avatar_url ?? null,
      detailPath:
        role === 'student'
          ? `/aluno/aulas/${bookingId}`
          : `/instrutor/aulas?chat=${bookingId}`,
    })
  }

  convos.sort(
    (a, b) =>
      new Date(b.lastMessage!.created_at).getTime() -
      new Date(a.lastMessage!.created_at).getTime(),
  )

  return convos
}

// ─── Conversation row ─────────────────────────────────────────────────────────

function ConvoRow({ c, role }: { c: Conversation; role: string }) {
  const timeAgo = c.lastMessage
    ? formatDistanceToNow(parseISO(c.lastMessage.created_at), {
        addSuffix: true,
        locale: ptBR,
      })
    : ''

  const name = c.otherName ?? (role === 'student' ? 'Instrutor' : 'Aluno')

  return (
    <Link
      to={c.detailPath}
      className={cn(
        'flex items-center gap-4 px-4 sm:px-6 py-4 border-b border-gray-100 hover:bg-muted/30 transition-colors',
        c.unreadCount > 0 && 'bg-brand-50/40 hover:bg-brand-50/60',
      )}
    >
      <div className="relative shrink-0">
        <Avatar className="h-11 w-11">
          <AvatarImage src={c.otherAvatar ?? undefined} />
          <AvatarFallback className="bg-brand-100 text-brand-700 font-semibold text-sm">
            {getInitials(name)}
          </AvatarFallback>
        </Avatar>
        {c.unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-brand-500 text-[9px] font-bold text-white">
            {c.unreadCount > 9 ? '9+' : c.unreadCount}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={cn('text-sm truncate', c.unreadCount > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-700')}>
            {name}
          </p>
          <span className="text-[11px] text-muted-foreground shrink-0">{timeAgo}</span>
        </div>
        <p className={cn('text-xs mt-0.5 truncate', c.unreadCount > 0 ? 'text-gray-700' : 'text-muted-foreground')}>
          {c.lastMessage?.content ?? ''}
        </p>
      </div>

      {c.unreadCount > 0 && (
        <Badge className="shrink-0 bg-brand-500 text-white text-[10px] px-1.5 py-0.5">
          {c.unreadCount}
        </Badge>
      )}
    </Link>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MessagesPage() {
  const { user, profile } = useAuth()
  const role = (profile?.role ?? 'student') as 'student' | 'instructor' | 'admin'
  const backPath = role === 'instructor' ? '/instrutor/dashboard' : '/aluno/dashboard'

  const { data: conversations, isLoading } = useQuery<Conversation[]>({
    queryKey: ['conversations', user?.id, role],
    enabled: !!user?.id,
    queryFn: () => fetchConversations(user!.id, role),
    staleTime: 1000 * 30,
  })

  const totalUnread = conversations?.reduce((s, c) => s + c.unreadCount, 0) ?? 0

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild className="shrink-0">
          <Link to={backPath}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            Mensagens
          </h1>
          {totalUnread > 0 && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {totalUnread} mensagem{totalUnread > 1 ? 'ens' : ''} não lida{totalUnread > 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="rounded-xl border border-gray-100 bg-white overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="divide-y divide-gray-100">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <Skeleton className="h-11 w-11 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        ) : !conversations || conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 mb-4">
              <MessageCircle className="h-7 w-7 text-gray-300" />
            </div>
            <p className="text-base font-semibold text-gray-700">Nenhuma conversa ainda</p>
            <p className="text-sm text-muted-foreground mt-1">
              As mensagens trocadas durante as aulas aparecem aqui.
            </p>
          </div>
        ) : (
          <div>
            {conversations.map((c) => (
              <ConvoRow key={c.bookingId} c={c} role={role} />
            ))}
            <div className="flex items-center justify-center gap-1.5 py-3 bg-gray-50">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-[11px] text-muted-foreground">
                {conversations.length} conversa{conversations.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
